import { FormEvent, useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

interface Pick { id: string; name: string }
interface Guide { id: string; slug: string; title: string; intro: string; pick_ids: string[]; published: boolean }
const EMPTY = { slug: "", title: "", intro: "", pick_ids: [] as string[], published: false };
const slugify = (t: string) => t.toLowerCase().replace(/\$/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 80);

function GuideForm({ picks, edit, onDone }: { picks: Pick[]; edit: Guide | null; onDone: () => void }) {
  const [f, setF] = useState(EMPTY);
  useEffect(() => {
    const ids = new Set(picks.map((p) => p.id));
    setF(edit ? { slug: edit.slug, title: edit.title, intro: edit.intro, pick_ids: edit.pick_ids.filter((i) => ids.has(i)), published: edit.published } : EMPTY);
  }, [edit]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const ids = new Set(picks.map((p) => p.id));
    setF((cur) => ({ ...cur, pick_ids: cur.pick_ids.filter((i) => ids.has(i)) }));
  }, [picks]);
  const toggle = (id: string) => setF({ ...f, pick_ids: f.pick_ids.includes(id) ? f.pick_ids.filter((x) => x !== id) : [...f.pick_ids, id] });
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (edit) await api.put(`/admin/affiliate/guides/${edit.id}`, f); else await api.post("/admin/affiliate/guides", f);
      toast.success(f.published ? "Guide saved and live" : "Guide saved as draft");
      setF(EMPTY);
      onDone();
    } catch (ex) { toast.error(formatApiError(ex)); }
  };
  return (
    <form onSubmit={submit} className="mt-5 space-y-3 rounded-2xl border-2 border-dashed border-[#23324A]/30 p-4" data-testid="guide-form">
      <input className="field" required placeholder="Title, e.g. Best handmade gifts under $25" value={f.title}
        onChange={(e) => setF({ ...f, title: e.target.value, slug: edit ? f.slug : slugify(e.target.value) })} data-testid="guide-title-input" />
      <div className="flex items-center gap-2 text-sm text-[#23324A]/70">/guides/<input className="field" required value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} data-testid="guide-slug-input" /></div>
      <textarea className="field min-h-[110px]" placeholder="Intro in your own words: who it's for, how you picked" value={f.intro} onChange={(e) => setF({ ...f, intro: e.target.value })} data-testid="guide-intro-input" />
      <p className="label">Picks, in the order you tap them</p>
      {picks.length === 0 ? <p className="text-xs text-[#23324A]/60">Add products under Supplies I use first.</p> : (
        <div className="flex flex-wrap gap-2">
          {picks.map((p) => {
            const n = f.pick_ids.indexOf(p.id);
            return <button type="button" key={p.id} className={`chip ${n >= 0 ? "chip-active" : ""}`} onClick={() => toggle(p.id)} data-testid={`guide-pick-toggle-${p.id}`}>{n >= 0 ? `${n + 1}. ` : ""}{p.name}</button>;
          })}
        </div>
      )}
      <label className="flex items-center gap-2 text-sm font-bold text-[#23324A]">
        <input type="checkbox" className="crayon-check" checked={f.published} onChange={(e) => setF({ ...f, published: e.target.checked })} data-testid="guide-published-check" />Live on the site
      </label>
      <div className="flex gap-3">
        <button className="btn-coral" type="submit" data-testid="guide-save-btn">{edit ? "Save guide" : "Create guide"}</button>
        {edit && <button type="button" className="btn-outline" onClick={onDone} data-testid="guide-cancel-btn">Cancel</button>}
      </div>
    </form>
  );
}

export function GuidesCard({ version = 0 }: { version?: number }) {
  const [guides, setGuides] = useState<Guide[]>([]);
  const [picks, setPicks] = useState<Pick[]>([]);
  const [edit, setEdit] = useState<Guide | null>(null);
  const load = useCallback(() => {
    api.get("/admin/affiliate/guides").then((r) => setGuides(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/affiliate/picks").then((r) => setPicks(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load, version]);
  const remove = async (g: Guide) => {
    if (!window.confirm(`Delete the guide "${g.title}"?`)) return;
    try { await api.delete(`/admin/affiliate/guides/${g.id}`); toast.success("Guide deleted"); if (edit?.id === g.id) setEdit(null); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="paper-card min-w-0 p-6" data-testid="aff-guides">
      <p className="font-display text-lg font-black text-[#23324A]">Gift guides</p>
      <p className="mt-1 text-sm text-[#23324A]/70">Pages like "Best gifts under $25", built from your Supplies I use picks. Prices aren't shown, so choose picks that fit the title.</p>
      <ul className="mt-4 space-y-2">
        {guides.length === 0 && <li className="text-sm text-[#23324A]/60" data-testid="guides-admin-empty">No guides yet.</li>}
        {guides.map((g) => (
          <li key={g.id} className="flex items-center gap-3 rounded-xl border-2 border-[#23324A]/15 bg-white px-3 py-2" data-testid={`guide-row-${g.slug}`}>
            <button className="min-w-0 flex-1 text-left" onClick={() => setEdit(g)} data-testid={`guide-edit-${g.slug}`}>
              <span className="block truncate font-semibold text-[#23324A]">{g.title}</span>
              <span className="text-xs text-[#23324A]/60">{g.published ? "Live" : "Draft"} · {g.pick_ids.length} {g.pick_ids.length === 1 ? "pick" : "picks"}</span>
            </button>
            {g.published && <a href={`/guides/${g.slug}`} target="_blank" rel="noreferrer" aria-label="Open" className="text-[#23324A]/60 hover:text-[#23324A]"><ExternalLink className="h-4 w-4" /></a>}
            <button onClick={() => remove(g)} aria-label="Delete" className="text-[#23324A]/60 hover:text-[#F08A6C]" data-testid={`guide-delete-${g.slug}`}><Trash2 className="h-4 w-4" /></button>
          </li>
        ))}
      </ul>
      <GuideForm picks={picks} edit={edit} onDone={() => { setEdit(null); load(); }} />
    </div>
  );
}
